// react
import { useContext } from 'react';

// types
import { Component, ComponentStatus } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextUserProjectPermissions } from '../../../pages/Project/Project.context';

// mui
import { Chip, Typography } from '@mui/material';

// ui
import { BtnMenu } from '../../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../../ui/ChipStatus/ChipStatus';
import { MenuItemProps } from '../../../ui/Menu/Menu';
import { Table, TableHeader, TableProps } from '../../../ui/Table/Table';
import { Time } from '../../../ui/Time/Time';

// dialogs
import { DialogEditComponent } from '../../../dialogs/DialogEditComponent/DialogEditComponent';
import { DialogUpdateComponentStatus } from '../../../dialogs/DialogUpdateComponentStatus/DialogUpdateComponentStatus';

// tables
import { TableBasicAllocations } from '../TableBasicAllocations/TableBasicAllocations';

// icons
import {
	DoDisturbOutlined as IconDoDisturb,
	ModeEditOutlined as IconModeEdit,
	PowerSettingsNew as IconPowerSettingsNew,
} from '@mui/icons-material';

// theme
import { theme } from '../../../../theme';

// config
import { featureFlags } from '../../../../config/featureFlags';

// props
export interface TableBasicComponentsProps extends Omit<TableProps, 'headers'> {
	data: Component[];
	refetchComponents?: () => void;
}

export const TableBasicComponents = ({
	data = [],
	refetchComponents,
	...props
}: TableBasicComponentsProps) => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// params
	const paramDialog = searchParams.get('dialog');

	const headers: TableHeader[] = [
		{
			label: t('name'),
			render: (name: string) => {
				return <Typography fontWeight={600}>{name}</Typography>;
			},
			value: 'name',
		},
		{
			label: t('parent'),
			render: (parent: number | Component) => {
				if (typeof parent === 'object' && parent?.name) {
					return <Typography>{parent.name}</Typography>;
				}
				return <Typography>-</Typography>;
			},
			value: 'parent',
		},
		{
			label: t('type'),
			render: (isDefault: boolean) => {
				return (
					<Chip
						color={isDefault ? 'info' : 'purple'}
						label={isDefault ? 'World Bank' : t('project')}
						size="small"
						variant="twoTone"
					/>
				);
			},
			value: 'isDefault',
		},
		{
			label: t('status'),
			render: (status: ComponentStatus) => {
				return <ChipStatus status={status} />;
			},
			value: 'status',
		},
		{
			label: t('created'),
			render: (created: string) => {
				return <Time date={created} />;
			},
			value: 'created',
		},
		{
			align: 'right',
			label: '',
			render: (name: string, header: TableHeader, component: Component) => {
				const btnMenuItems = [];

				if (userProjectPermissions?.component?.update) {
					btnMenuItems.push({
						children: t('editComponent'),
						icon: <IconModeEdit />,
						onClick: () => {
							searchParams.set('dialog', 'editComponent');
							searchParams.set('dialogComponent', String(component.id));
							setSearchParams(searchParams);
						},
					});

					const btnUpdateStatus: MenuItemProps = {
						children: t('deactivateComponent'),
						icon: <IconDoDisturb color="error" />,
						onClick: () => {
							searchParams.set('dialog', 'updateComponentStatus');
							searchParams.set('dialogComponent', String(component.id));
							searchParams.set('dialogStatus', 'DEACTIVATED');
							setSearchParams(searchParams);
						},
					};

					if (component.status === 'DEACTIVATED') {
						btnUpdateStatus.children = t('activateComponent');
						btnUpdateStatus.icon = <IconPowerSettingsNew color="success" />;
						btnUpdateStatus.onClick = () => {
							searchParams.set('dialog', 'updateComponentStatus');
							searchParams.set('dialogComponent', String(component.id));
							searchParams.set('dialogStatus', 'ACTIVE');
							setSearchParams(searchParams);
						};
					}

					btnMenuItems.push(btnUpdateStatus);
				}

				if (btnMenuItems.length) {
					return <BtnMenu items={btnMenuItems} />;
				}

				return <></>;
			},
			value: 'created',
		},
	];

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogComponent');
		searchParams.delete('dialogStatus');
		setSearchParams(searchParams);
	};

	const dataEnhanced = data.map((item, i) => {
		const isLast = i === data.length - 1;

		let expandedContent;

		if (featureFlags['IFRS']) {
			expandedContent = (
				<TableBasicAllocations
					data={item.allocations || []}
					sxTable={{
						borderBottomColor: theme.palette.divider,
						borderBottomStyle: 'solid',
						borderBottomWidth: isLast ? 0 : 1,
					}}
				/>
			);
		}
		return {
			...item,
			expandedContent,
		};
	});

	return (
		<>
			<Table {...props} data={dataEnhanced} headers={headers} />
			<DialogEditComponent
				isOpen={paramDialog === 'editComponent'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchComponents) {
						refetchComponents();
					}
				}}
			/>
			<DialogUpdateComponentStatus
				isOpen={paramDialog === 'updateComponentStatus'}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchComponents) {
						refetchComponents();
					}
				}}
			/>
		</>
	);
};
